import { createContext, type FunctionComponent, type PropsWithChildren, useState } from 'react';

export type AlertNotificationType = 'success' | 'info' | 'danger' | 'warning' | 'grey';

export type ShowNotificationFn = (msg: string | JSX.Element, type: AlertNotificationType, timeout: number, onDismiss?: () => void) => void;
type AlertNotificationState = {
	message: string | JSX.Element;
	type: AlertNotificationType;
	winTimeout: number | null;
	showNotification: ShowNotificationFn;
	closeNotification: () => void;
	onDismiss?: () => void;
};

export const AlertNotificationContext = createContext<AlertNotificationState>({
	message: '',
	type: 'success',
	winTimeout: null,
	showNotification: () => {},
	closeNotification: () => {},
	onDismiss: () => {}
});

export const AlertNotificationProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
	const defaultState: AlertNotificationState = {
		winTimeout: null,
		type: 'success',
		message: '',
		showNotification: (msg: string | JSX.Element, type: AlertNotificationType, timeout: number, onDismiss?: () => void) => {
			const winTimeout = timeout
				? window.setTimeout(() => {
						setNotifyState((prevState) => {
							return { ...prevState, message: '', winTimeout: null };
						});
				  }, timeout)
				: null;
			setNotifyState((prevState) => {
				if (prevState.winTimeout) {
					window.clearTimeout(prevState.winTimeout);
				}
				return { ...prevState, message: msg, type, winTimeout, onDismiss };
			});
		},
		closeNotification: () => {
			setNotifyState((prevState) => {
				if (prevState.winTimeout) {
					window.clearTimeout(prevState.winTimeout);
				}
				return { ...prevState, message: '', winTimeout: null };
			});
		}
	};

	const [notifyState, setNotifyState] = useState<AlertNotificationState>(defaultState);

	return <AlertNotificationContext.Provider value={notifyState}>{children}</AlertNotificationContext.Provider>;
};
